<template>
	<div class="d-flex flex-column gap-16px w-100">
		<section
			v-if="!isFirstTimeUser"
			id="hideinmobile"
			class="mrp-firstcard mob-w-100 d-flex flex-column align-items-start justify-content-start gap-10px pointer"
			@click="goToProfileVisibility"
		>
			<!-- Avatar stack: up to 3 recruiters + "+N" overflow badge in the 4th circle -->
			<div class="mrp-avatars" :aria-label="`${avatarCount} recruiters`">
				<template v-if="isLoadingProfileView">
					<b-skeleton
						class="mrp-avatar-skeleton"
						width="37px"
						height="37px"
					></b-skeleton>
					<b-skeleton
						class="mrp-avatar-skeleton"
						width="37px"
						height="37px"
					></b-skeleton>
					<b-skeleton
						class="mrp-avatar-skeleton"
						width="37px"
						height="37px"
					></b-skeleton>
				</template>
				<template v-else>
					<div
						v-for="(av, index) in visibleAvatars"
						:key="'avt-' + index"
						class="mrp-avatar"
						:class="['mrp-avatar-' + index]"
					>
						<span v-if="av.initial" class="mrp-avatar-initial">{{
							av.initial
						}}</span>
						<img
							v-else
							:alt="av.label"
							:src="av.image"
							@error="onAvatarError"
						/>
					</div>
					<div
						v-if="overflowCount > 0"
						class="mrp-avatar mrp-avatar-more"
						title="More recruiters viewed your profile"
					>
						+{{ overflowCount }}
					</div>
				</template>
			</div>
			<div class="fw-M fam-inter fs-15 color-1F1F1F">
				<b-skeleton
					v-if="isLoadingProfileView"
					width="180px"
					height="18px"
				></b-skeleton>
				<template v-else>
					{{ profileViewCountDisplay }}
					{{ profileViewCountDisplay === 1 ? "Recruiter" : "Recruiters" }}
					viewed your profile
				</template>
			</div>
			<div class="fw-M fs-14 fam-mont color-newblue mrp-see-who-viewed">
				See who viewed
				<img
					src="../../assets/NewDashboard/sidebar/bluearw-forward.svg"
					alt=""
				/>
			</div>
			<div class="fam-inter fw-R fs-13 color-5E6B81">
				<b-skeleton
					v-if="isLoadingProfileView"
					width="140px"
					height="14px"
				></b-skeleton>
				<template v-else>
					<span v-if="showWeekMonthBreakdown">
						<template v-if="thisWeekViews > 0">
							{{ thisWeekViews }} this week
							<template v-if="thisMonthViews > 0"> • </template>
						</template>
						<template v-if="thisMonthViews > 0">
							{{ thisMonthViews }} this month
						</template>
					</span>
				</template>
			</div>
		</section>
		<section
			class="w-100 pointer"
			id="hideinmobile"
			@click="openInstantInterviewModal"
		>
			<img
				src="../../assets/NewDashboard/sidebar/promo-banner.png"
				class="w-100 h-100"
				alt=""
			/>
		</section>
		<section class="mrp-thirdcard">
			<div class="fam-mont fw-M fs-14 color-6A6A6A">COMPANY ASSESSMENTS</div>
			<section
				class="d-flex align-items-center flex-wrap gap-10px justify-content-center mt-16px company-grid-container"
			>
				<template v-if="isLoadingCompanyAssessments">
					<section
						v-for="(item, index) in companySkeletons"
						:key="'skel-' + index"
						class="d-flex align-items-center flex-column justify-content-center pointer company-assessment-item"
					>
						<b-skeleton
							width="44px"
							height="44px"
							class="rounded mb-8px"
						></b-skeleton>
						<b-skeleton width="70px" height="12px" class="mb-8px"></b-skeleton>
						<b-skeleton width="50px" height="12px"></b-skeleton>
					</section>
				</template>
				<template v-else>
					<section
						v-for="(company, index) in dashboardCompanies"
						:key="company.subCategoryID ?? 'see-all'"
						class="d-flex align-items-center flex-column justify-content-center pointer company-assessment-item"
						@click="goToCompanyAssessment(company)"
					>
						<div
							class="mrp-company-cards d-flex align-items-center justify-content-center"
						>
							<div
								class="mrp-inner-cc d-flex align-items-center justify-content-center"
							>
								<img
									:src="company.icon"
									:alt="company.name"
									class="overflow-hidden"
									:class="!company.isSeeAll && 'br-50'"
									@error="onCompanyIconError($event, index)"
								/>
							</div>
						</div>
						<div class="fw-M fam-mont fs-11 color-1F1F1F mt-10px company-name">
							{{ company.name }}
						</div>
						<div class="fw-LM fam-mont fs-11 color-596C98">
							{{ company.tests }}
						</div>
					</section>
				</template>
			</section>
		</section>

		<PaymentComingSoonModal
			:showModal.sync="showPaymentComingSoonModal"
			:fromInstantInterview="true"
		/>
	</div>
</template>

<script>
import { getProfileViews, getCompanyAssessment } from "@/utils/apiFunctions.js";
import PaymentComingSoonModal from "@/views/MyProfile/PaymentComingSoonModal.vue";
import { getFallbackProfileStats } from "@/utils/fallbackViews.js";
import EventBus from "@/utils/EventBus";

// Fallback visitor counts are generated per user by fallbackViews.js (random
// and stable), so the dashboard still displays a realistic set of numbers when
// the real recruiter count is 0.

export default {
	components: {
		PaymentComingSoonModal,
	},
	computed: {
		// When real count is 0, still show a realistic number of visitors
		useFallbackProfileViews() {
			return this.profileViewCount <= 0;
		},
		// Random, stable per-user fallback numbers (total/week/month). Kept in one
		// object so the count, week and month shown always stay consistent.
		fallbackViewStats() {
			return getFallbackProfileStats();
		},
		// Show at most 3 avatar circles; the rest roll into a "+N" overflow badge.
		visibleAvatarLimit() {
			return 3;
		},

		profileViewCountDisplay() {
			return this.useFallbackProfileViews
				? this.fallbackViewStats.total
				: this.profileViewCount;
		},

		// Count driving the avatar stack. Uses the SAME number shown in the card
		// text (profileViewCountDisplay), so when the API returns 0 and the text
		// falls back to a realistic count (2-6), this many persona avatars render.
		avatarCount() {
			return this.profileViewCountDisplay || 0;
		},

		visibleAvatars() {
			const avatars = [];
			const limit = this.visibleAvatarLimit;
			const shown = Math.min(this.avatarCount, limit);

			for (let i = 0; i < shown; i++) {
				const view = this.profileViews[i] || {};
				const name = typeof view.viewerName === "string" ? view.viewerName : "";

				avatars.push({
					label: name || "Recruiter",
					initial: this.initialOf(name),
					image: this.bindProfileImage(i),
				});
			}

			return avatars;
		},

		overflowCount() {
			return Math.max(
				this.profileViewCountDisplay - this.visibleAvatarLimit,
				0
			);
		},
		thisWeekViews() {
			if (this.useFallbackProfileViews) {
				return this.fallbackViewStats.week;
			}
			return this.profileViewData?.recruiterViewsThisWeek ?? 0;
		},
		thisMonthViews() {
			if (this.useFallbackProfileViews) {
				return this.fallbackViewStats.month;
			}
			return this.profileViewData?.recruiterViewsThisMonth ?? 0;
		},
		showWeekMonthBreakdown() {
			if (this.useFallbackProfileViews) {
				return this.thisWeekViews > 0 || this.thisMonthViews > 0;
			}
			const d = this.profileViewData;
			return !!(
				d &&
				(d.recruiterViewsThisWeek > 0 || d.recruiterViewsThisMonth > 0)
			);
		},
		// Subcategories returned by getCompanyAssessment, mapped for display.
		// Each subcategory is treated as a "company" in the grid. A real image
		// comes from the API when available; otherwise a dummy icon is shown.
		displayCompanies() {
			if (!Array.isArray(this.companyAssessments)) return [];
			return this.companyAssessments.map((s, i) => {
				const count = s.assessmentCount;
				return {
					name: s.subCategoryTitle,
					subCategoryID: s.subCategoryID,
					categoryID: this.companyCategoryID,
					tests: count == null ? "" : `${count} test${count === 1 ? "" : "s"}`,
					icon:
						s.image ||
						this.dummyCompanyIcons[i % this.dummyCompanyIcons.length],
				};
			});
		},
		// Show up to 7 company tiles and ALWAYS append a trailing "See All" tile
		// so users can always reach the full category. When the category has
		// fewer than 7 assessments the tile stays bare (no count); when it has
		// more than 7 the remaining count = totalAssessments - 7 is shown below.
		dashboardCompanies() {
			const companies = this.displayCompanies.slice(0, 7);
			const more = Math.max(this.displayCompanies.length - 7, 0);
			const total = Number(this.companyTotalAssessments) || 0;
			const remaining = Math.max(total - 1, 0);

			companies.push({
				name: "See All",
				icon: require("@/assets/NewDashboard/rightside/seeall.png"),
				tests: remaining > 0 ? `${remaining}+ tests` : "",
				categoryID: this.companyCategoryID,
				subCategoryID: null,
				isSeeAll: true,
			});

			return companies;
		},
		// Placeholder tiles shown while company assessments are still loading.
		// Mirrors the company assessment loop: up to 7 companies + "See All" = 8
		// tiles, laid out 3 columns per row => rows of 3,3,2.
		companySkeletons() {
			return Array(8).fill({});
		},
		isFirstTimeUser() {
			return this.$store.getters["userVUEX/getIsFirstTimeUser"];
		},
	},
	data() {
		return {
			showPaymentComingSoonModal: false,
			profileViewData: null,
			profileViewCount: 0,
			profileViews: [],
			isLoadingProfileView: true,
			// Company/skill assessments returned by getCompanyAssessment
			companyAssessments: [],
			companyCategoryID: "",
			companyTotalAssessments: 0,
			isLoadingCompanyAssessments: false,
			// Dummy icons used until the API returns real images. Only zomato.png
			// currently exists in the assets folder, so it is used as the fallback.
			dummyCompanyIcons: [require("@/assets/NewDashboard/rightside/asmnt.png")],
		};
	},
	beforeDestroy() {
		EventBus.$off("login-success", this.createdActions);
		EventBus.$off("onboarding-fetch-post-login", this.createdActions);
	},
	created() {
		EventBus.$on("login-success", this.createdActions);
		EventBus.$on("onboarding-fetch-post-login", this.createdActions);
		this.createdActions();
	},
	methods: {
		createdActions() {
			this.fetchProfileViews();
			this.fetchCompanyAssessments();
		},
		async fetchProfileViews() {
			try {
				this.isLoadingProfileView = true;
				const payload = {
					count: 10,
					page: 0,
				};
				const res = await getProfileViews(payload);
				if (res && res.status === 200) {
					const data = res.data?.data || res.data || {};
					this.profileViewData = data;
					this.profileViews = Array.isArray(data.views) ? data.views : [];
					const rawCount =
						data.recruiterViews !== undefined
							? data.recruiterViews
							: data.recruiterViewCount !== undefined
							? data.recruiterViewCount
							: data.profileViewCount !== undefined
							? data.profileViewCount
							: Array.isArray(data.views)
							? data.views.length
							: data.total || 0;

					this.profileViewCount = Number(rawCount) || 0;
				}
			} catch (err) {
				console.error("Error fetching profile visit log:", err);
			} finally {
				this.isLoadingProfileView = false;
			}
		},
		// Blurred persona profile photo, keyed by stack position (1-26) to keep
		// the same CDN + index scheme used by the Profile Visibility page.
		bindProfileImage(index) {
			return `https://cvstaticfiles.s3.ap-south-1.amazonaws.com/candidate/profilePictureBlurred/profile${
				(index % 26) + 1
			}.png`;
		},
		// "John Doe" -> "JD", "Alice" -> "A" (best-effort uppercase initials).
		initialOf(name) {
			if (!name) return "";
			const parts = String(name).trim().split(/\s+/).filter(Boolean);
			if (!parts.length) return "";
			const first = parts[0].charAt(0);
			const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
			return (first + last).toUpperCase();
		},
		// If a persona photo fails to load, swap to the local fallback icon so the
		// circle is never empty.
		onAvatarError(event) {
			if (event.target.classList.contains("mrp-avatar-img-fallback")) return;
			event.target.classList.add("mrp-avatar-img-fallback");
			event.target.src = require("@/assets/NewDashboard/sidebar/resumeprofile.svg");
		},
		goToProfileVisibility() {
			this.$router.push({ name: "ProfileVisibility" }).catch(() => {});
		},
		async fetchCompanyAssessments() {
			this.isLoadingCompanyAssessments = true;
			try {
				const res = await getCompanyAssessment();
				if (res && res.status === 200) {
					const data = res.data?.data || {};
					this.companyCategoryID = data.categoryID || "";
					this.companyTotalAssessments = Number(data.totalAssessments) || 0;
					this.companyAssessments = data.subcategories || [];
				}
			} catch (err) {
				console.error("Error fetching company assessments:", err);
			} finally {
				this.isLoadingCompanyAssessments = false;
			}
		},
		// If a subcategory image fails to load, swap to the local dummy fallback
		// so the circle is never empty.
		onCompanyIconError(event, index) {
			event.target.src =
				this.dummyCompanyIcons[index % this.dummyCompanyIcons.length || 0];
		},
		goToCompanyAssessment(company) {
			// Ignore clicks on skeleton placeholders while data is still loading
			if (this.isLoadingCompanyAssessments || !company) return;
			const query = {
				categoryIDs: JSON.stringify([company.categoryID]),
				from: "companyAssessments",
			};
			// "See All" routes to the whole category with no subcategory filter
			if (company.subCategoryID) {
				query.subCategoryIDs = JSON.stringify([company.subCategoryID]);
			}
			this.$router
				.push({ name: "CandidateAssessmentProgress", query })
				.catch(() => {});
		},
		goToAssessmentMarketplace() {
			this.$router.push({ name: "AssessmentMarketplace" }).catch(() => {});
		},
		openInstantInterviewModal() {
			this.showPaymentComingSoonModal = true;
		},
	},
};
</script>

<style scoped>
.br-50 {
	border-radius: 50% !important;
}
.color-596C98 {
	color: #596c98;
}
.mt-8px {
	margin-top: 8px;
}
.color-5E6B81 {
	color: #5e6b81;
}
.mrp-firstcard {
	padding: 15.0528px;
	background: linear-gradient(260.28deg, #d8dfed 0.7%, #f7f9fe 99.3%);
	border: 1px solid #bbcae7;
	box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.12);
	border-radius: 15.0528px;
}
/* Avatar stack (recruiters who viewed the profile) */
.mrp-avatars {
	display: flex;
	align-items: center;
}
.mrp-avatar {
	position: relative;
	width: 37px;
	height: 37px;
	border-radius: 50%;
	border: 2px solid #e8edf6;
	background: #2d5cc2;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
	margin-left: -10px; /* overlaps the previous circle */
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}
.mrp-avatar:first-child {
	margin-left: 0;
}
.mrp-avatar img {
	width: 100%;
	height: 100%;
	object-fit: cover; /* real persona photos fill the circle */
	box-sizing: border-box;
}
.mrp-avatar img.mrp-avatar-img-fallback {
	object-fit: contain;
	padding: 9px; /* keep the fallback icon inside the circle */
	filter: brightness(0) invert(1); /* default dark icon -> white on the blue circle */
}
.mrp-avatar-initial {
	color: #ffffff;
	font-family: var(--fam-mont, "Montserrat", sans-serif);
	font-weight: 600;
	font-size: 15px;
	line-height: 1;
}
.mrp-avatar-0 {
	background: #4a79e2;
}
.mrp-avatar-1 {
	background: #3a6bd6;
}
.mrp-avatar-2 {
	background: #2d5cc2;
}
.mrp-avatar-more {
	background: #eaf0fd;
	color: #20639b;
	font-family: var(--fam-mont, "Montserrat", sans-serif);
	font-weight: 600;
	font-size: 13px;
	border-color: #e8edf6;
}
.mrp-avatar-skeleton {
	border-radius: 50% !important;
	margin-left: -10px;
}
.mrp-avatar-skeleton:first-child {
	margin-left: 0;
}
.mrp-see-who-viewed {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}
.mrp-see-who-viewed img {
	width: 14px;
	height: 14px;
	object-fit: contain;
}
.mrp-secondcard {
	overflow: hidden;
	height: 149px;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
	border-radius: 16px;
}
.mrp-thirdcard {
	padding: 16px;
	border: 1px solid #e9e9e9;
	/*filter: drop-shadow(0px -2px 5px rgba(0, 0, 0, 0.1))
		drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1));*/
	border-radius: 24px;
}
.mb-12px {
	margin-bottom: 12px;
}
.company-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px 0px;
}
.company-grid-last {
	display: flex;
	justify-content: center;
	gap: 8px;
	margin-top: 16px;
}
.company-grid-last > div {
	flex: 0 0 calc(33.33% - 6px);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}
.company-grid-mobile {
	display: none;
}
#showinmobile {
	display: none;
}
.mrp-company-cards {
	width: 65px;
	height: 65px;
	background: #ffffff;
	border: 1px solid #e9e9e9;
	box-shadow: 0px -2px 5px rgba(255, 255, 255, 0.9),
		0px 4px 10px rgba(0, 0, 0, 0.1);
	border-radius: 100px;
}
.mrp-inner-cc {
	width: 60px;
	height: 60px;
	border: 1px solid #f1f1f1;
	box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.06);
	border-radius: 100px;
}
@media only screen and (max-width: 1028px) {
	.mrp-thirdcard {
		border: unset;
		padding: 0;
	}
	.company-grid-mobile {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16px 0px;
	}
	#showinmobile {
		display: block;
	}
	.company-assessment-item {
		flex: 1 !important;
	}
	.company-grid-container {
        display: grid !important;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        justify-content: center;
    }
    .company-grid-container .company-assessment-item {
        width: 100%;
    }
}
.company-assessment-item {
	flex: 0 0 calc((100% - 32px) / 3);
}
/* Skeleton tiles must mirror the company assessment loop: always 3-per-row
	(rows of 3,3,2) even on narrow widths where the media query above forces
	flex:1 onto the real tiles. Placed after it so this !important wins for
	skeletons only, leaving the real grid and mobile behavior unchanged. */
.company-assessment-skeleton {
	flex: 0 0 calc((100% - 32px) / 3) !important;
}
.company-name {
	max-width: 65px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
